<script setup lang="ts">
import { computed, useSlots } from "vue"
import type { CSSProperties, HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { Separator, type SeparatorProps } from "reka-ui"
import { cn } from "@/shared/utils"

interface SeparatorGradientProps {
  class?: HTMLAttributes["class"]
  gradient?: boolean
  gradientFrom?: string
  gradientVia?: string
  gradientTo?: string
}

const props = withDefaults(
  defineProps<SeparatorProps & SeparatorGradientProps>(),
  {
    orientation: "horizontal",
    decorative: true,
    gradient: false,
  },
)

const slots = useSlots()

const delegatedProps = reactiveOmit(
  props,
  "class",
  "gradient",
  "gradientFrom",
  "gradientVia",
  "gradientTo",
)

const hasDefaultSlot = computed(() => Boolean(slots.default?.().length))
const isVertical = computed(() => props.orientation === "vertical")

const gradientStyle = computed<CSSProperties | undefined>(() => {
  if (!props.gradient) {
    return undefined
  }

  const direction = isVertical.value ? "to bottom" : "to right"
  const from = props.gradientFrom ?? "var(--border)"
  const via = props.gradientVia ?? "var(--primary)"
  const to = props.gradientTo ?? "var(--border)"

  return {
    backgroundImage: `linear-gradient(${direction}, ${from}, ${via}, ${to})`,
    backgroundColor: "transparent",
  }
})
</script>

<template>
  <Separator
    v-if="!hasDefaultSlot"
    data-slot="separator-root"
    v-bind="delegatedProps"
    :style="gradientStyle"
    :class="
      cn(
        `shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px`,
        !props.gradient && 'bg-border',
        props.class,
      )
    "
  />

  <Separator
    v-else
    data-slot="separator-root"
    v-bind="delegatedProps"
    :class="
      cn(
        'shrink-0',
        isVertical
          ? 'flex h-full flex-col items-center gap-2'
          : 'flex w-full items-center gap-2',
        props.class,
      )
    "
  >
    <span
      data-slot="separator-segment-start"
      aria-hidden="true"
      :style="gradientStyle"
      :class="
        cn(
          'shrink-0',
          isVertical ? 'w-px flex-1' : 'h-px flex-1',
          !props.gradient && 'bg-border',
        )
      "
    />

    <span data-slot="separator-content" class="shrink-0">
      <slot />
    </span>

    <span
      data-slot="separator-segment-end"
      aria-hidden="true"
      :style="gradientStyle"
      :class="
        cn(
          'shrink-0',
          isVertical ? 'w-px flex-1' : 'h-px flex-1',
          !props.gradient && 'bg-border',
        )
      "
    />
  </Separator>
</template>
